import { useMemo } from 'react';
import {
    COMPACT_MIN_FEED_WIDTH,
    computeMeetingLayout,
    LayoutResult,
    SCREEN_SHARE_ASPECT,
} from 'flow/utils/meeting-layout';
import useComponentSize from './useComponentSize';

interface MeetingLayoutInput {
    /** Feeds destined for the feed grid / strip (participants without a stage role). */
    feedCount: number;
    /** Pinned feeds (form the stage when no screen is shared). */
    pinnedCount?: number;
    /** True when a screen share should occupy the stage. */
    screenShareActive?: boolean;
    /**
     * Cap on rendered feed tiles (bounds live video decodes). The "+N" overflow
     * still counts against `feedCount`, so it reflects every feed not shown.
     */
    maxVisibleFeeds?: number;
    /**
     * Measured aspect ratio (w/h) of the shared screen. Falls back to
     * {@link SCREEN_SHARE_ASPECT} until the real source ratio is known.
     */
    screenAspect?: number | null;
    /**
     * Compact dock (thumbnail/sidebar): use a smaller feed minimum so a few
     * faces fit side by side in the narrow pane. Independent of {@link feedOnly}
     * — the dock can still split (e.g. a pinned stage) while keeping small tiles.
     */
    compact?: boolean;
    /** Force a FEED grid (no stage), regardless of pins / screen share. */
    feedOnly?: boolean;
}

/**
 * Measures the Main-area container and computes the tile layout from it.
 *
 * Returns a `ref` to attach to the Main-area element (its measured box is the
 * single source of truth for sizing — no manual footer/right-panel arithmetic),
 * the measured `size`, and the computed `layout` (rects relative to the box).
 */
export const useMeetingLayout = ({
    feedCount,
    pinnedCount = 0,
    screenShareActive = false,
    screenAspect: measuredScreenAspect = null,
    compact = false,
    feedOnly = false,
    maxVisibleFeeds,
}: MeetingLayoutInput): {
    ref: ReturnType<typeof useComponentSize>[0];
    size: ReturnType<typeof useComponentSize>[1];
    layout: LayoutResult;
} => {
    const [ref, size] = useComponentSize();
    const screenAspect = screenShareActive
        ? (measuredScreenAspect ?? SCREEN_SHARE_ASPECT)
        : null;

    const layout = useMemo(
        () =>
            computeMeetingLayout({
                container: size,
                feedCount,
                pinnedCount,
                screenAspect,
                feedOnly,
                minFeedWidth: compact ? COMPACT_MIN_FEED_WIDTH : undefined,
                maxVisibleFeeds,
            }),
        [
            size,
            feedCount,
            pinnedCount,
            screenAspect,
            compact,
            feedOnly,
            maxVisibleFeeds,
        ],
    );

    return { ref, size, layout };
};

export default useMeetingLayout;
